/**
 * A one-line suggestion the person can pick: a 14px icon and a short label on
 * the surface stat cards use. Spec beside it in suggestion-chip.md.
 *
 * @module components/ui/suggestion-chip
 */
import { useState, type ComponentProps, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { CARD_SURFACE_CLASS } from "./stat-card";

export function SuggestionChip({
  icon,
  label,
  className,
  ...props
}: Omit<ComponentProps<"button">, "children"> & {
  readonly icon: ReactNode;
  readonly label: string;
}) {
  const [firstLabel] = useState(label);
  return (
    <button
      type="button"
      className={cn(CARD_SURFACE_CLASS, "group/suggestion-chip flex h-10 max-w-full min-w-0 items-center gap-2 px-3.5 text-left hover:-translate-y-px motion-reduce:hover:translate-y-0", className)}
      {...props}
      data-slot="suggestion-chip"
    >
      <span className="flex shrink-0 items-center [&>svg]:size-3.5 [&>svg]:stroke-[1.75]">{icon}</span>
      {/* Keyed so a label that replaces the first one fades in; the first never does. */}
      <span
        key={label}
        className={cn(
          "min-w-0 truncate text-[14px] text-foreground/85 group-hover/suggestion-chip:text-foreground",
          label !== firstLabel && "motion-safe:animate-canvas-fade-in motion-safe:[animation-duration:150ms]",
        )}
      >
        {label}
      </span>
    </button>
  );
}
